import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Partner, User, UserCategory, Veteran } from '@app/entity';
import { TokenStorageService } from '@app/core/services/token-storage.service';
import 'rxjs/add/operator/do';
import 'rxjs/add/observable/throw';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { loadWithRetries } from '@app/core/utils';
import { API_VERSION } from '@app/core/api.version';

@Injectable()
export class AuthService {
  private userStream = new ReplaySubject<User>(1);
  private currentUser: User;

  /**
   * user vs userId
   *
   * The user observable emits user profiles when they get updated (and not necessarily changed).
   * If you need just user id, you should use userId observable instead, because
   * it emits values only when user id *realy* changes. Especially if you load
   * organization/volunteer profiles in that observable chain, because they might emit
   * updated profiles to the user observable and cause an infinite loop
   * (emit->request->update->emit->...).
   */
  public user = this.userStream.asObservable();
  public userId = this.user.pipe(
    map(user => user && user.id || null),
    distinctUntilChanged(),
  );

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
  ) {
    this.user.subscribe(user => this.currentUser = user);
    this.fetchCurrentUser().subscribe();
  }

  private fetchCurrentUser(): Observable<User> {
    let userObservable = Observable.of(null);
    if (this.hasToken()) {
      userObservable = loadWithRetries(() => this.http.get<User>(`/${API_VERSION}/auth/me/`)
        .catch(error => (401 <= error.status && error.status <= 404)
          ? Observable.of(null) : Observable.throw(error)
        ));
    }
    return userObservable
      .do(user => {
        this.userStream.next(user);
        if (!user) this.tokenStorage.setToken(null);
      });
  }

  hasToken(): boolean {
    return !!this.tokenStorage.getToken();
  }

  login(email: string, password: string): Observable<User> {
    return this.http.post<User>(`/${API_VERSION}/auth/login/`, { email, password })
      .do((user: User) => {
        this.userStream.next(user);
        this.tokenStorage.saveForumUsername(user.email);
        this.tokenStorage.saveForumToken(user['forum_auth']);
      });
  }

  registerPartner(partner: Partner, password: string): Observable<User> {
    return this.http.post<User>(`/${API_VERSION}/partners/`, { ...partner, password })
      .do((user: User) => this.userStream.next(user));
  }

  registerVeteran(veteran: Veteran, password: string): Observable<User> {
    return this.http.post<User>(`/${API_VERSION}/veterans/`, { ...veteran, password })
      .do((user: User) => {
        this.userStream.next(user);
        this.tokenStorage.saveForumUsername(user.email);
        this.tokenStorage.saveForumToken(user['forum_auth']);
      });
  }

  changePassword(user: User, old_password: string, new_password: string) {
    return this.http.post<any>(`/${API_VERSION}/${user.user_type}s/change_password/`,
      { old_password: old_password, new_password: new_password });
  }

  resetPassword(password: string, token: string) {
    return this.http.post(`/${API_VERSION}/auth/set_password/`, { password: password, token: token })
      .do((user: User) => this.userStream.next(user));
  }

  forgotPassword(email: string) {
    return this.http.post(`/${API_VERSION}/auth/recover/`, { email: email });
  }

  logout() {
    this.tokenStorage.saveForumUsername(null);
    this.tokenStorage.saveForumToken(null);
    this.tokenStorage.setToken(null);
    this.userStream.next(null);
  }

  clearForumAuth() {
    this.tokenStorage.saveForumUsername(null);
    this.tokenStorage.saveForumToken(null);
  }

  updateIfIsCurrentUser(user: User) {
    if (this.currentUser && user && this.currentUser.id === user.id) {
      this.userStream.next({...user});
    }
  }

  updateProfile(data: User): Observable<User> {
    if (!data || !data.user_type) return Observable.of(data);
    return this.http.patch<User>(`/${API_VERSION}/${data.user_type}s/${encodeURIComponent('' + data.id)}/`, data)
      .do(user => this.updateIfIsCurrentUser(user));
  }

  getCategories(user_type: string): Observable<UserCategory[]> {
    return this.http.get<UserCategory[]>(`/${API_VERSION}/${user_type}_categories/`);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/auth.service.ts